import { useRef, useState } from 'react';

import { cn } from '@utils/cn';

import { Button, Icon, Text } from '@ui-component-library';

import type { FileUploadProps } from './FileUpload.types';

const FileUpload = ({
  accept,
  value,
  onChange,
  validate,
  label,
  errorText,
  className,
  description,
}: FileUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isInvalid, setIsInvalid] = useState(false);
  const subText = description || "Dra och släpp eller klicka här"
  const processFile = async (file: File) => {
    if (validate) {
      const isValid = await validate(file);
      setIsInvalid(!isValid);
      if (!isValid) {
        onChange(null);
        return;
      }
    }
    onChange(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleRemoveFile = () => {
    onChange(null);
    setIsInvalid(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event?.dataTransfer?.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  return (
    <div className={cn('bg-white-50 flex flex-col gap-4 rounded-lg p-4', className)}>
      <div
        className="relative flex w-full cursor-pointer flex-row items-center justify-between"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="pointer-events-none absolute top-0 left-0 z-10 h-full w-full cursor-pointer opacity-0"
        />
        <div className="flex flex-row items-start justify-between gap-3">
        {value && !isInvalid ? (
          <Icon name="UploadFilled" className={'fill-[#349269]'} size="medium" />
        ) : (
          <Icon name="UploadSimple" className={'fill-charcoal-600'} size="large" />
        )}
          <div className="flex flex-col gap-1">
            <Text variant="text-sm/leading-normal/medium">{label}</Text>
            <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
              {subText}
            </Text>
          </div>
        </div>
        <Text
          variant="text-sm/leading-normal/medium"
          className="py-1-5 rounded-md bg-[#F5F5F5] px-3 text-[#1A1A1A]"
        >
          Välj fil
        </Text>
      </div>

      {isInvalid && (
        <>
          <div className="w-full border-b border-solid border-[#E2E8F0]" />
          <div className="gap-3-5 flex flex-row items-start justify-between">
            <Icon name="ExclamationFilled" className="fill-[#C63A35]" size="large" />
            <div className="flex flex-col justify-start gap-2">
              <Text variant="text-sm/leading-normal/medium" className="truncate">
                {errorText.title}
              </Text>
              <Text variant="text-xs/leading-normal/normal" className="text-wrap text-[#737373]">
                {errorText.description}
              </Text>
            </div>
          </div>
        </>
      )}

      {value && !isInvalid && (
        <>
          <div className=" w-full border-b border-solid border-[#E2E8F0]" />
          <div className="gap-3-5 flex flex-row items-center justify-between">
            <div className="gap-3-5 flex min-w-0 flex-row items-center justify-start">
              <Icon name="CheckFilled" className="fill-[#349269]" size="large" />
              <Text variant="text-sm/leading-normal/medium" className="truncate">
                {value.name}
              </Text>
            </div>
            <Button variant="tertiary" size="icon" onClick={handleRemoveFile}>
              <Icon name="CrossSimple" className="fill-[#0F0F0F]" size="large" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export { FileUpload };
